//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Checkmark,
  Close,
  ErrorFilled,
  Renew,
  Warning,
} from '@carbon/icons-react';
import { useTranslation } from 'react-i18next';
import type {
  UpgradeTaskSummary,
  UpgradePreviewTask,
  UpgradeResultPayload,
} from './upgradeTypes';

type UpgradeLoopModalProps = {
  isOpen: boolean;
  onClose: () => void;
  loopId: string;
  loopName: string;
  onUpgrade: () => Promise<UpgradeResultPayload>;
  onAnimationComplete?: (hasAnyFailure: boolean) => void;
  upgradeTasks: UpgradeTaskSummary[];
  previewTasks: UpgradePreviewTask[];
  isPreviewLoading: boolean;
  previewError: string;
};

type UpgradeStage = 'confirm' | 'upgrading' | 'complete';

type ProgressItem = {
  id: number;
  name: string;
  status: string;
  progress: number;
  color: string;
  error?: boolean;
};

const DEFAULT_PROGRESS_NAMES = [
  'plan',
  'build',
  'deploy',
  'measure',
  'infrastructure',
];

const getInitialProgressItems = (
  previewTasks: UpgradePreviewTask[] = [],
  t: (key: string) => string,
): ProgressItem[] => {
  const taskNames =
    previewTasks.length > 0
      ? previewTasks.map((task) => task.name)
      : DEFAULT_PROGRESS_NAMES;

  return taskNames.map((name, index) => ({
    id: index + 1,
    name,
    status: t('upgrade-waiting-in-queue'),
    progress: 0,
    color: 'bg-gray-300',
  }));
};

// Module-level Map to persist upgrade state per loop instance
// Key is loopId (not loopName) to prevent collisions when loops have the same display name
const globalUpgradeState = new Map<
  string,
  {
    upgradeInProgress: boolean;
    upgradeStage: UpgradeStage;
    progressItems: ProgressItem[];
  }
>();

const getGlobalState = (loopId: string) => {
  if (!globalUpgradeState.has(loopId)) {
    globalUpgradeState.set(loopId, {
      upgradeInProgress: false,
      upgradeStage: 'confirm',
      progressItems: [],
    });
  }
  return globalUpgradeState.get(loopId)!;
};

export default function UpgradeLoopModal({
  isOpen,
  onClose,
  loopId,
  loopName,
  onUpgrade,
  onAnimationComplete,
  upgradeTasks,
  previewTasks,
  isPreviewLoading,
  previewError,
}: UpgradeLoopModalProps) {
  const { t } = useTranslation();
  // Get instance-specific global state (keyed by loopId to prevent name collisions)
  const globalState = getGlobalState(loopId);

  // Get target version - prioritize preview (available immediately), then upgradeTasks (after completion)
  const targetVersion =
    (previewTasks.length > 0 && previewTasks[0].targetVersion) ||
    (upgradeTasks.length > 0 && upgradeTasks[0].loopVersionAtExecution) ||
    '2.0'; // Fallback only if both are unavailable

  // Initialize from instance-specific global state to persist across remounts
  const [upgradeStage, _setUpgradeStage] = useState<UpgradeStage>(
    globalState.upgradeStage,
  );

  // Wrap setUpgradeStage to sync with instance-specific global state
  const setUpgradeStage = useCallback(
    (newStage: UpgradeStage) => {
      globalState.upgradeStage = newStage;
      _setUpgradeStage(newStage);
    },
    [loopId, globalState],
  );

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Refs for focus management and keyboard accessibility
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);
  const [progressItems, _setProgressItems] = useState<ProgressItem[]>(
    globalState.progressItems.length > 0
      ? globalState.progressItems
      : getInitialProgressItems(previewTasks, t),
  );

  // Wrap setProgressItems to sync with instance-specific global state
  const setProgressItems = useCallback(
    (items: ProgressItem[] | ((prev: ProgressItem[]) => ProgressItem[])) => {
      _setProgressItems((prev) => {
        const newItems = typeof items === 'function' ? items(prev) : items;
        globalState.progressItems = newItems;
        return newItems;
      });
    },
    [globalState, loopId],
  );
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const upgradeInProgressRef = useRef(globalState.upgradeInProgress);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current = [];
  }, []);

  const resetState = useCallback(() => {
    // Don't reset if upgrade is actively in progress (animations running)
    // Use instance-specific global state to check, not local state
    if (
      globalState.upgradeInProgress &&
      globalState.upgradeStage === 'upgrading'
    ) {
      return;
    }
    clearTimers();
    upgradeInProgressRef.current = false;
    globalState.upgradeInProgress = false;
    globalState.upgradeStage = 'confirm';
    globalState.progressItems = [];
    setShowCancelConfirm(false);
    setShowCloseWarning(false);
    _setUpgradeStage('confirm');
    setHasError(false);
    setIsSubmitting(false);
    setProgressItems(getInitialProgressItems(previewTasks, t));
    // Clean up global state to prevent memory leak
    globalUpgradeState.delete(loopId);
  }, [
    clearTimers,
    previewTasks,
    setProgressItems,
    upgradeStage,
    loopId,
    globalState,
  ]);

  const updateProgress = useCallback(
    (
      index: number,
      status: string,
      progress: number,
      color: string,
      error = false,
    ) => {
      setProgressItems((prev) =>
        prev.map((item, idx) =>
          idx === index ? { ...item, status, progress, color, error } : item,
        ),
      );
    },
    [],
  );

  const runSuccessFlow = useCallback(
    (tasks: UpgradeTaskSummary[]) => {
      clearTimers();

      // Animate each task completion with delays (2.5s per task)
      // Use index directly - don't try to match by name since backend names are different
      tasks.forEach((task, index) => {
        const timer = setTimeout(
          () => {
            const isSuccess = task.status === 'COMPLETED';
            const isFailed = task.status === 'FAILED';

            // Update only status and progress, keep the original task name from preview
            updateProgress(
              index,
              isSuccess
                ? t('upgrade-task-complete')
                : isFailed
                  ? t('upgrade-task-failed')
                  : task.status,
              100,
              isSuccess
                ? 'bg-green-500'
                : isFailed
                  ? 'bg-red-500'
                  : 'bg-blue-500',
              isFailed,
            );

            // After all tasks, check if any failed before moving to complete stage
            if (index === tasks.length - 1) {
              const innerTimer = setTimeout(() => {
                const hasAnyFailure = tasks.some(
                  (t) => t.status?.toUpperCase() === 'FAILED',
                );

                if (hasAnyFailure) {
                  // Animations are done, allow modal to be closed
                  upgradeInProgressRef.current = false;
                  globalState.upgradeInProgress = false;
                  // Don't change stage - stay in upgrading to show retry button
                } else {
                  // Set stage to complete but keep globalUpgradeInProgress true
                  // This prevents the modal from closing until user clicks Done
                  globalState.upgradeStage = 'complete';
                  _setUpgradeStage('complete');
                }
                onAnimationComplete?.(hasAnyFailure);
              }, 1000);
              // Track inner timer to prevent leak on unmount
              timersRef.current.push(innerTimer);
            }
          },
          (index + 1) * 2500, // 2.5 seconds per task (slower animation)
        );
        timersRef.current.push(timer);
      });
    },
    [clearTimers, updateProgress, loopId, globalState, t, onAnimationComplete],
  );

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  useEffect(() => {
    // Only reset progress items if we're in confirm stage AND not upgrading
    if (upgradeStage === 'confirm' && !globalState.upgradeInProgress) {
      setProgressItems(getInitialProgressItems(previewTasks, t));
    }
  }, [previewTasks, upgradeStage, loopId, globalState, setProgressItems]);

  const handleCancelUpgrade = useCallback(() => {
    setShowCancelConfirm(true);
  }, []);

  const confirmCancelUpgrade = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleClose = useCallback(() => {
    // Use instance-specific global stage instead of local state to avoid race conditions
    const currentStage = globalState.upgradeStage;

    // Only prevent closing if upgrade is ACTIVELY in progress (animations running)
    // If animations are done (globalUpgradeInProgress = false), allow closing even from upgrading stage
    if (currentStage === 'upgrading' && globalState.upgradeInProgress) {
      setShowCloseWarning(true);
      return;
    }

    // Allow closing from confirm stage or upgrading stage (if animations done)
    if (currentStage === 'confirm' || currentStage === 'upgrading') {
      resetState();
      onClose();
    }
  }, [onClose, resetState, upgradeStage, loopId, globalState]);

  const confirmClose = useCallback(() => {
    // Force reset state even if upgrade is in progress, since user explicitly chose to close
    clearTimers();
    upgradeInProgressRef.current = false;
    globalState.upgradeInProgress = false;
    globalState.upgradeStage = 'confirm';
    globalState.progressItems = [];
    setShowCancelConfirm(false);
    setShowCloseWarning(false);
    _setUpgradeStage('confirm');
    setHasError(false);
    setIsSubmitting(false);
    setProgressItems(getInitialProgressItems(previewTasks, t));
    // Clean up global state to prevent memory leak
    globalUpgradeState.delete(loopId);
    onClose();
  }, [
    onClose,
    clearTimers,
    previewTasks,
    setProgressItems,
    globalState,
    loopId,
  ]);

  // Keyboard accessibility: Focus management and Escape key handling
  useEffect(() => {
    if (!isOpen) return;

    // Store the currently focused element to restore later
    previousActiveElementRef.current = document.activeElement as HTMLElement;

    // Set initial focus to the modal
    const focusModal = () => {
      if (modalRef.current) {
        // Try to focus the first focusable element, or the modal itself
        const focusableElements = modalRef.current.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusableElements.length > 0) {
          (focusableElements[0] as HTMLElement).focus();
        } else {
          modalRef.current.focus();
        }
      }
    };

    // Small delay to ensure modal is rendered
    const timeoutId = setTimeout(focusModal, 10);

    // Handle Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };

    // Focus trap: keep focus within modal
    const handleFocusTrap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !modalRef.current) return;

      const focusableElements = modalRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[
        focusableElements.length - 1
      ] as HTMLElement;

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', handleFocusTrap);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleFocusTrap);

      // Restore focus to the element that opened the modal
      if (previousActiveElementRef.current) {
        previousActiveElementRef.current.focus();
      }
    };
  }, [isOpen, handleClose]);

  const startUpgrade = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    upgradeInProgressRef.current = true;
    globalState.upgradeInProgress = true;
    setUpgradeStage('upgrading');
    setHasError(false);
    setIsSubmitting(true);

    // Use preview tasks for initial display (will be updated when API returns)
    const initialItems = getInitialProgressItems(previewTasks, t);
    setProgressItems(initialItems);

    const failedIndex = Math.min(1, Math.max(initialItems.length - 1, 0));

    try {
      const result = await onUpgrade();

      // Check if we have task details (even on failure)
      const hasTasks = result.tasks && result.tasks.length > 0;

      if (!result.success && !hasTasks) {
        // Complete failure with no task details (e.g., 500 error, network error)
        // Show generic error on one task
        updateProgress(
          failedIndex,
          t('upgrade-task-failed'),
          100,
          'bg-red-500',
          true,
        );
        setHasError(true);
        return;
      }

      // We have tasks - either all succeeded or partial success (207)
      // Keep the preview task names, just update the count if needed

      // Always synchronize progressItems to match result.tasks.length
      // This prevents out-of-bounds errors in runSuccessFlow
      const adjustedItems = result.tasks.map((task, index) => ({
        id: index + 1,
        name: previewTasks[index]?.name || `Task ${index + 1}`,
        status: t('upgrade-waiting-in-queue'),
        progress: 0,
        color: 'bg-gray-300',
      }));
      setProgressItems(adjustedItems);

      // Check if any tasks failed
      const hasFailures = result.tasks.some(
        (t) => t.status?.toUpperCase() === 'FAILED',
      );
      if (hasFailures) {
        setHasError(true); // Show retry button
      }

      // Small delay to ensure state update before animations
      // This will show both successful (green) and failed (red) tasks
      setTimeout(() => {
        runSuccessFlow(result.tasks);
      }, 100);
    } catch (error) {
      // Network error or unexpected exception
      // Don't clear globalUpgradeInProgress - keep modal open to show error
      // User can retry or close manually
      updateProgress(failedIndex, 'Upgrade Failed', 100, 'bg-red-500', true);
      setHasError(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    onUpgrade,
    previewTasks,
    runSuccessFlow,
    updateProgress,
    loopId,
    globalState,
    setUpgradeStage,
    t,
  ]);

  const handleRetry = useCallback(async () => {
    setProgressItems(getInitialProgressItems(previewTasks, t));
    setHasError(false);
    await startUpgrade();
  }, [previewTasks, startUpgrade]);

  // Keep modal open if upgrade is in progress, even if parent wants to close it
  // Use instance-specific global state which persists across component unmounts
  if (!isOpen && !globalState.upgradeInProgress) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleClose}
      role="presentation"
    >
      <div
        ref={modalRef}
        className="relative mx-4 w-full max-w-3xl rounded-lg bg-white shadow-xl dark:bg-[#262626]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Upgrade Loop Modal"
        tabIndex={-1}
      >
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#f59e0b]">
              <Warning size={24} className="text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('upgrade-loop-to-version', {
                loopName,
                version: targetVersion,
              })}
            </h2>
          </div>
          {upgradeStage !== 'complete' && (
            <button
              onClick={handleClose}
              className="rounded p-1 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Close"
            >
              <Close size={20} className="text-gray-600 dark:text-gray-400" />
            </button>
          )}
        </div>

        <div className="flex min-h-[300px] flex-col px-6 py-4">
          {upgradeStage === 'confirm' && (
            <>
              <p className="mb-4 text-gray-700 dark:text-gray-300">
                {t('upgrade-updates-description')}
              </p>

              <div>
                <h3 className="mb-1 text-base font-semibold text-gray-900 dark:text-white">
                  {t('upgrade-infrastructure')}
                </h3>
                {isPreviewLoading ? (
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {t('upgrade-loading-steps')}
                  </p>
                ) : previewError ? (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {previewError}
                  </p>
                ) : previewTasks.length > 0 ? (
                  <div className="mt-3 space-y-2">
                    {previewTasks.map((task) => (
                      <div
                        key={task.id}
                        className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800"
                      >
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {task.name}
                          </p>
                          <span className="rounded bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                            {task.action}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                          {t('upgrade-latest-status', {
                            status: task.latestStatus,
                          })}
                        </p>
                        {task.reason ? (
                          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                            {task.reason}
                          </p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {t('upgrade-no-steps-required')}
                  </p>
                )}
              </div>
            </>
          )}

          {upgradeStage === 'upgrading' && (
            <>
              <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                {t('upgrade-in-progress-title')}
              </h3>
              <div className="space-y-3">
                {progressItems.map((item) => (
                  <div
                    key={item.id}
                    className="grid grid-cols-12 items-center gap-4"
                  >
                    <div className="col-span-3">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {item.name}
                      </span>
                    </div>
                    <div className="col-span-9">
                      <div className="mb-1 flex items-center gap-2">
                        {item.error ? (
                          <>
                            <ErrorFilled size={16} className="text-red-600" />
                            <span className="text-xs text-red-600 dark:text-red-400">
                              {item.status}
                            </span>
                          </>
                        ) : item.progress === 100 ? (
                          <>
                            <Checkmark size={16} className="text-green-600" />
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              {item.status}
                            </span>
                          </>
                        ) : (
                          <span className="text-xs text-gray-500 dark:text-gray-500">
                            {t('upgrade-status-label', { status: item.status })}
                          </span>
                        )}
                      </div>
                      <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                        <div
                          className={`h-2 rounded-full transition-all duration-1000 ${item.color}`}
                          style={{ width: `${item.progress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {upgradeStage === 'complete' && (
            <div className="py-4 text-center">
              <div className="mb-3">
                <svg
                  width="60"
                  height="60"
                  viewBox="0 0 80 80"
                  fill="none"
                  className="mx-auto"
                >
                  <circle cx="40" cy="40" r="40" fill="#24a148" opacity="0.1" />
                  <circle cx="40" cy="40" r="30" fill="#24a148" />
                  <path
                    d="M25 40L35 50L55 30"
                    stroke="white"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-semibold text-gray-900 dark:text-white">
                {t('upgrade-complete-title')}
              </h3>
              <p className="mx-auto mb-4 max-w-md text-sm text-gray-600 dark:text-gray-400">
                {t('upgrade-complete-description', {
                  loopName,
                  version: targetVersion,
                })}
              </p>

              {/* Summary with real data */}
              <div className="mx-auto max-w-md rounded-lg bg-gray-50 p-4 text-left dark:bg-gray-800">
                <h4 className="mb-2 text-xs font-semibold text-gray-900 dark:text-white">
                  {t('upgrade-summary')}
                </h4>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      {t('upgrade-components')}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {upgradeTasks.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      {t('upgrade-version')}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {targetVersion}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-t border-gray-200 pt-2 dark:border-gray-700">
                    <span className="text-gray-600 dark:text-gray-400">
                      {t('upgrade-status')}
                    </span>
                    <span className="font-medium text-green-600 dark:text-green-400">
                      {t('upgrade-status-active')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex-1" />
        </div>

        {showCancelConfirm && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-black/50">
            <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-[#333333]">
              <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">
                {t('upgrade-cancel-confirm-title')}
              </h3>
              <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
                {t('upgrade-cancel-confirm-message')}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  className="btn-secondary"
                >
                  {t('upgrade-continue')}
                </button>
                <button onClick={confirmCancelUpgrade} className="btn-alert">
                  {t('upgrade-yes-cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {showCloseWarning && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-black/50">
            <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-[#333333]">
              <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">
                {t('upgrade-close-warning-title')}
              </h3>
              <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
                {t('upgrade-close-warning-message')}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowCloseWarning(false)}
                  className="btn-secondary"
                >
                  {t('cancel')}
                </button>
                <button onClick={confirmClose} className="btn-primary">
                  {t('close')}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="modal-footer">
          {upgradeStage === 'confirm' && (
            <>
              <button
                onClick={handleCancelUpgrade}
                className="btn-secondary"
                disabled={isSubmitting}
              >
                {t('cancel')}
              </button>
              <button
                onClick={startUpgrade}
                className="btn-alert"
                disabled={isSubmitting}
              >
                {t('upgrade-yes-upgrade')}
              </button>
            </>
          )}
          {upgradeStage === 'upgrading' && (
            <>
              {hasError ? (
                <button
                  onClick={handleRetry}
                  className="btn-primary flex items-center gap-2"
                  disabled={isSubmitting}
                >
                  <Renew size={16} />
                  {t('upgrade-retry')}
                </button>
              ) : (
                <div className="h-10" />
              )}
            </>
          )}
          {upgradeStage === 'complete' && (
            <button
              onClick={() => {
                resetState();
                onClose();
              }}
              className="btn-primary"
            >
              {t('upgrade-done')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
